const DEEPSEEK_API_KEY = 'sk-04a8bc1279a0475eaab02f745561042b';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

class DeepSeekEssayService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.apiUrl = DEEPSEEK_API_URL;
  }

  // Pricing copied to keep UI cost display consistent with knowledgeGraph service
  DEEPSEEK_TARIFF = {
    standard: {
      deepseek_chat: { input_cached: 0.5, input_uncached: 2.0, output: 8.0 },
      deepseek_reasoner: { input_cached: 1.0, input_uncached: 4.0, output: 16.0 }
    },
    discount: {
      deepseek_chat: { input_cached: 0.25, input_uncached: 1.0, output: 4.0 },
      deepseek_reasoner: { input_cached: 0.25, input_uncached: 1.0, output: 4.0 }
    }
  };

  isDiscountTime() {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000));
    const hour = beijingTime.getHours();
    return (hour >= 0 && hour < 8) || (hour >= 0 && hour < 8.5);
  }

  calculateCost(inputTokens, outputTokens, model = 'deepseek-chat', isCached = false) {
    const tariff = this.isDiscountTime() ? this.DEEPSEEK_TARIFF.discount : this.DEEPSEEK_TARIFF.standard;
    const modelKey = model === 'deepseek-reasoner' ? 'deepseek_reasoner' : 'deepseek_chat';
    const inputRate = isCached ? tariff[modelKey].input_cached : tariff[modelKey].input_uncached;
    const outputRate = tariff[modelKey].output;
    const inputCost = (inputTokens / 1_000_000) * inputRate;
    const outputCost = (outputTokens / 1_000_000) * outputRate;
    return {
      inputCost: parseFloat(inputCost.toFixed(6)),
      outputCost: parseFloat(outputCost.toFixed(6)),
      totalCost: parseFloat((inputCost + outputCost).toFixed(6)),
      isDiscountTime: this.isDiscountTime(),
      model
    };
  }

  async generateEssay(prompt, options = {}) {
    try {
      const {
        maxTokens = 2000,
        temperature = 0.7,
        language = 'traditional',
        essayType = 'general',
        targetLength = 500
      } = options;

      // Enhanced prompt construction
      const enhancedPrompt = this.constructEnhancedPrompt(prompt, {
        language,
        essayType,
        targetLength
      });

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: this.getSystemPrompt(language, essayType)
            },
            {
              role: 'user',
              content: enhancedPrompt
            }
          ],
          max_tokens: maxTokens,
          temperature: temperature,
          top_p: 0.9,
          frequency_penalty: 0.1,
          presence_penalty: 0.1
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid response format from DeepSeek API');
      }

      const generatedContent = data.choices[0].message.content;
      
      return {
        success: true,
        content: generatedContent,
        usage: data.usage,
        model: data.model,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return {
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  constructEnhancedPrompt(userPrompt, options) {
    const { language, essayType, targetLength } = options;
    
    let enhancedPrompt = userPrompt;
    
    // Add language-specific instructions
    if (language === 'traditional') {
      enhancedPrompt += `\n\n請使用繁體中文撰寫，確保用詞準確、語法正確。`;
    } else {
      enhancedPrompt += `\n\n請使用簡體中文撰寫，確保用詞準確、語法正確。`;
    }

    // Add essay type specific instructions
    switch (essayType) {
      case 'argumentative':
        enhancedPrompt += `\n\n這是一篇議論文，請確保：\n- 論點清晰明確\n- 提供充分的論據和例子\n- 結構完整（引言、正文、結論）\n- 語言正式且具有說服力`;
        break;
      case 'narrative':
        enhancedPrompt += `\n\n這是一篇記敘文，請確保：\n- 情節生動有趣\n- 描寫細膩具體\n- 人物形象鮮明\n- 語言生動形象`;
        break;
      case 'descriptive':
        enhancedPrompt += `\n\n這是一篇描寫文，請確保：\n- 描寫細緻入微\n- 運用多種修辭手法\n- 語言優美生動\n- 營造鮮明的意境`;
        break;
      case 'letter':
        enhancedPrompt += `\n\n這是一封信件，請確保：\n- 格式正確（稱謂、正文、結尾、署名）\n- 語調適當\n- 內容完整\n- 表達真誠`;
        break;
      default:
        enhancedPrompt += `\n\n請確保文章結構完整，內容充實，語言流暢。`;
    }

    // Add length requirement
    if (targetLength) {
      enhancedPrompt += `\n\n目標字數：約${targetLength}字。請嚴格控制字數，確保在目標範圍內。`;
    }

    return enhancedPrompt;
  }

  getSystemPrompt(language, essayType) {
    const basePrompt = language === 'traditional' 
      ? '你是一位專業的中文寫作老師，擅長撰寫各種類型的中文文章。請根據用戶的要求，撰寫高質量的中文文章。'
      : '你是一位专业的中文写作老师，擅长撰写各种类型的中文文章。请根据用户的要求，撰写高质量的中文文章。';

    const typeSpecificPrompt = this.getTypeSpecificPrompt(essayType, language);
    
    return `${basePrompt}\n\n${typeSpecificPrompt}\n\n請確保：\n- 文章結構完整\n- 內容充實有意義\n- 語言流暢自然\n- 符合中文寫作規範`;
  }

  getTypeSpecificPrompt(essayType, language) {
    const prompts = {
      argumentative: language === 'traditional' 
        ? '議論文要求：論點明確，論據充分，邏輯清晰，具有說服力。'
        : '议论文要求：论点明确，论据充分，逻辑清晰，具有说服力。',
      narrative: language === 'traditional'
        ? '記敘文要求：情節生動，描寫細膩，人物鮮明，語言生動。'
        : '记叙文要求：情节生动，描写细腻，人物鲜明，语言生动。',
      descriptive: language === 'traditional'
        ? '描寫文要求：描寫細緻，意境優美，語言生動，富有感染力。'
        : '描写文要求：描写细致，意境优美，语言生动，富有感染力。',
      letter: language === 'traditional'
        ? '書信要求：格式正確，語調適當，內容完整，表達真誠。'
        : '书信要求：格式正确，语调适当，内容完整，表达真诚。',
      default: language === 'traditional'
        ? '一般文章要求：結構完整，內容充實，語言流暢。'
        : '一般文章要求：结构完整，内容充实，语言流畅。'
    };

    return prompts[essayType] || prompts.default;
  }

  // Parse response and extract useful information
  parseResponse(response) {
    if (!response.success) {
      return {
        success: false,
        error: response.error,
        content: null,
        metadata: null
      };
    }

    const content = response.content;
    const metadata = {
      wordCount: this.countChineseCharacters(content),
      paragraphCount: this.countParagraphs(content),
      estimatedReadingTime: this.calculateReadingTime(content),
      model: response.model,
      usage: response.usage,
      timestamp: response.timestamp
    };

    return {
      success: true,
      content,
      metadata
    };
  }

  countChineseCharacters(text) {
    // Count Chinese characters, punctuation, and spaces
    const chineseRegex = /[\u4e00-\u9fff\u3400-\u4dbf\u20000-\u2a6df\u2a700-\u2b73f\u2b740-\u2b81f\u2b820-\u2ceaf\uf900-\ufaff\u3300-\u33ff\ufe30-\ufe4f\uf900-\ufaff\u3300-\u33ff\ufe30-\ufe4f]/g;
    const matches = text.match(chineseRegex);
    return matches ? matches.length : 0;
  }

  countParagraphs(text) {
    return text.split('\n\n').filter(para => para.trim().length > 0).length;
  }

  calculateReadingTime(text) {
    const wordCount = this.countChineseCharacters(text);
    const wordsPerMinute = 200; // Average reading speed for Chinese
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes;
  }
}

export default new DeepSeekEssayService(); 